import { createSlice } from "@reduxjs/toolkit";

const SLICE_NAME = "customGridView";

const initialState = {
  // Structure: { featureGridView: { id: rowData } }
  // ID-based storage (no page/index dependency)
  selectionsByFeature: {},
  // Store grid scroll position to restore after sort/page changes
  // Structure: { featureGridView: { scrollLeft, scrollTop } }
  scrollPositionsByFeature: {},
};

const customGridViewSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    selectRow(state, action) {
      const { data, featureGridView, idKey = "lenderBoardId" } = action.payload;

      // Ensure feature object exists
      if (!state.selectionsByFeature[featureGridView]) {
        state.selectionsByFeature[featureGridView] = {};
      }

      // Add row by ID
      const id = data[idKey];
      if (id !== undefined && id !== null) {
        state.selectionsByFeature[featureGridView][id] = data;
      }
    },

    selectRows(state, action) {
      const { data, featureGridView, idKey = "lenderBoardId" } = action.payload;

      // Ensure feature object exists
      if (!state.selectionsByFeature[featureGridView]) {
        state.selectionsByFeature[featureGridView] = {};
      }

      // Bulk add: iterate through data items and store by ID
      data.forEach(item => {
        const id = item[idKey];
        if (id !== undefined && id !== null) {
          state.selectionsByFeature[featureGridView][id] = item;
        }
      });
    },

    deselectRow(state, action) {
      const { data, featureGridView, idKey = "lenderBoardId" } = action.payload;

      if (!state.selectionsByFeature[featureGridView]) {
        return;
      }

      // Remove row by ID
      const id = data?.[idKey];
      if (id !== undefined && id !== null) {
        delete state.selectionsByFeature[featureGridView][id];
      }

      // Clean up empty feature entries
      if (Object.keys(state.selectionsByFeature[featureGridView]).length === 0) {
        delete state.selectionsByFeature[featureGridView];
      }
    },

    deselectRows(state, action) {
      const { data, featureGridView, idKey = "lenderBoardId" } = action.payload;

      if (!state.selectionsByFeature[featureGridView]) {
        return;
      }

      // Bulk remove: delete each row by ID
      data.forEach(item => {
        const id = item?.[idKey];
        if (id !== undefined && id !== null) {
          delete state.selectionsByFeature[featureGridView][id];
        }
      });

      // Clean up empty feature entries
      if (Object.keys(state.selectionsByFeature[featureGridView]).length === 0) {
        delete state.selectionsByFeature[featureGridView];
      }
    },

    clearSelection(state, action) {
      const { featureGridView } = action.payload;

      if (state.selectionsByFeature[featureGridView]) {
        delete state.selectionsByFeature[featureGridView];
      }
    },

    clearAllSelections(state, action) {
      const { featureGridView } = action.payload;

      if (state.selectionsByFeature[featureGridView]) {
        delete state.selectionsByFeature[featureGridView];
      }
    },

    clearAllSelectionsForAllFeatures() {
      return initialState;
    },

    setScrollPosition(state, action) {
      const { scrollLeft, scrollTop, featureGridView } = action.payload;

      // Ensure feature scroll position object exists
      if (!state.scrollPositionsByFeature[featureGridView]) {
        state.scrollPositionsByFeature[featureGridView] = {
          scrollLeft: 0,
          scrollTop: 0,
        };
      }

      state.scrollPositionsByFeature[featureGridView] = {
        scrollLeft: scrollLeft ?? state.scrollPositionsByFeature[featureGridView].scrollLeft,
        scrollTop: scrollTop ?? state.scrollPositionsByFeature[featureGridView].scrollTop,
      };
    },

    clearScrollPosition(state, action) {
      const { featureGridView } = action.payload;

      if (state.scrollPositionsByFeature[featureGridView]) {
        delete state.scrollPositionsByFeature[featureGridView];
      }
    },

    clearAllScrollPositions(state) {
      state.scrollPositionsByFeature = {};
    },
  },
});

export const {
  selectRow,
  selectRows,
  deselectRow,
  deselectRows,
  clearSelection,
  clearAllSelections,
  clearAllSelectionsForAllFeatures,
  setScrollPosition,
  clearScrollPosition,
  clearAllScrollPositions,
} = customGridViewSlice.actions;

export default customGridViewSlice.reducer;
